import ZionForm, { useZionForm } from '@fs/zion-form'
import { ControlSplit } from '@fs/zion-icon'
import {
  useSlsHighlightsContext,
  useSlsStateContext,
  getImage,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  getPersonIdsForField,
} from '@fs/zion-sls'
import { Button, Divider, H6, Separator, useOverlay } from '@fs/zion-ui'
import cloneDeep from 'lodash.clonedeep'
import uniqBy from 'lodash.uniqby'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import UnsavedChangesOverlay from '../../../../../components/generic/panels/shared/UnsavedChangesOverlay'
import { useR2DispatchContext, useR2StateContext } from '../../../../../providers/r2Provider/R2Context'
import useUnsavedChangesOverlay from '../../../../../hooks/useUnsavedChangesOverlay'
import useRecords from '../../../hooks/useRecords'
import handleSubmitSharedFieldForm from '../../../services/actions/submitHandlers'
import PanelHeader from '../PanelHeader'
import SharedFieldPersonCheckbox from './SharedFieldPersonCheckbox'
import { getDisplayField, getFormDefaultValues, getPersonsSharingField } from './SharedFieldUtils'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const buildAllAvailablePersonsOptions = ({
  hasAdditionalPersonsOptions,
  currentRecordOrSharingFieldPersons,
  imagePersonsMinusCurrentRecord,
  allPersonsSharingField,
  allPersonIdsSharingFieldOnImages,
}) =>
  // We need to deduplicate the list of persons sharing the field and all persons on the image
  uniqBy(
    hasAdditionalPersonsOptions
      ? [...currentRecordOrSharingFieldPersons, ...imagePersonsMinusCurrentRecord]
      : allPersonsSharingField,
    'id'
  ).sort(
    // Sorting elements to put the ones that are sharing the field at the top of the list
    (a, b) => allPersonIdsSharingFieldOnImages.includes(b.id) - allPersonIdsSharingFieldOnImages.includes(a.id)
  )

function renderOtherPagesSections({
  allTheArksExceptCurrent,
  thumbnails,
  currentArkIndex,
  t,
  r2Dispatch,
  lockSharedSuperEl,
  newValue,
  currentRecord,
  sharedElement,
}) {
  return (
    Object.entries(allTheArksExceptCurrent)
      // This filter statement keeps us from attempting to load a page that isnt directly adjacent to current
      // This is necessary, because as of now, we don't load any other pages into state, so we don't have their data
      .filter(([ark]) => {
        const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
        return Math.abs(currentArkIndex - arkIndex) === 1
      })
      .map(([ark, persons]) => {
        // Shouldn't this be object notation?
        const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
        const pageNumber = arkIndex + 1
        return (
          <>
            <Separator size="xs" />
            <Divider
              label={t('edit-shared-field.names-on-page-n.label', 'Names on Page {pageNumber}', {
                pageNumber,
              })}
            />
            <Button
              Icon={ControlSplit}
              onClick={() =>
                r2Dispatch({
                  type: 'OPEN_SPLIT_SCREEN_TO_PAGE',
                  value: pageNumber,
                })
              }
              emphasis="medium"
            >
              {t('edit-shared-field.view-image-in-context.close.button', 'View in Context')}
            </Button>
            {persons?.map((person) => (
              <SharedFieldPersonCheckbox
                key={`${ark}-${person.id}`}
                person={person}
                lockSharedSuperEl={lockSharedSuperEl}
                newValue={newValue}
                record={currentRecord}
                sharedElement={sharedElement}
              />
            ))}
          </>
        )
      })
  )
}

const SharedFieldPage2 = ({ handleSave, sharedFieldValue, saveButtonRef, onDiscard }) => {
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [t] = useTranslation()

  const currentArkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === state.currentImageArk)
  const unsavedChangesOverlay = useOverlay()
  const h = useSlsHighlightsContext()
  const newValue = sharedFieldValue
  const currentImage = getImage(state?.images, state?.currentImageArk)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], r2State.selectedPersonId)
  const sharedElement = getNestedOrFloatingElementById(state.images, r2State.editingSharedFieldId)
  const fieldDisplayValue = getMostRecentFieldValue(getDisplayField(sharedElement, sharedElement?.subElements?.[0]))

  const newValueText = useMemo(
    () => (typeof newValue === 'object' ? newValue.standard?.standardizedText || newValue.originalText : newValue),
    [newValue]
  )

  const title = useMemo(
    () =>
      fieldDisplayValue
        ? t('edit-shared-field.heading', '{fieldType}: {fieldValue}', {
            fieldValue: newValueText,
            fieldType: sharedElement?.typeData?.displayLabel,
          })
        : '',
    [fieldDisplayValue, newValueText, sharedElement?.typeData?.displayLabel, t]
  )

  const hasAdditionalPersonsOptions = useMemo(
    () =>
      sharedElement.fieldType === 'NAME_SURN' ||
      sharedElement.elementType === 'NAME_SURNAME' ||
      sharedElement.typeData.elementTypes.includes('PERSON'),
    [sharedElement]
  )

  const allPersonIdsSharingFieldOnImages = useMemo(() => {
    if (sharedElement) {
      return getPersonIdsForField(sharedElement, state.images)
    }
    return null
  }, [sharedElement, state.images])

  const allPersonsSharingField = useMemo(
    () => getPersonsSharingField(allPersonIdsSharingFieldOnImages, state.images),
    [allPersonIdsSharingFieldOnImages, state.images]
  )

  // The number of people attached to a shared super element such as Date, Place, or Event cannot be changed
  const testElement = getNestedOrFloatingElementById(state.images, sharedElement?.superElements?.[0]?.id)
  const lockSharedSuperEl = useMemo(
    () => testElement.elementType !== 'PERSON' && testElement.elementType !== 'NAME_SURNAME',
    [testElement]
  )

  const { allRecords } = useRecords({
    images: state?.images,
    currentImageArk: state?.currentImageArk,
    personIdsOrder: state?.personOrder?.find((po) => po.partitionKey === state?.currentImageArk)?.personIds,
    thumbnails,
  })

  const currentRecord = useMemo(
    () => allRecords?.find((rec) => rec.id === sharedElement?.recordId),
    [allRecords, sharedElement?.recordId]
  )

  const personElementsFromCurrentRecord = useMemo(
    () => currentRecord?.subElements?.filter((element) => element.elementType === 'PERSON') || [],
    [currentRecord]
  )

  const currentRecordOrSharingFieldPersons = useMemo(
    () => uniqBy([...personElementsFromCurrentRecord, ...allPersonsSharingField], 'id'),
    [personElementsFromCurrentRecord, allPersonsSharingField]
  )

  const allPersonsOnImage = useMemo(
    () =>
      allRecords?.flatMap(
        (record) => record.subElements?.filter((element) => element.elementType === 'PERSON') || []
      ) || [],
    [allRecords]
  )

  const imagePersonsMinusCurrentRecord = useMemo(
    () => allPersonsOnImage?.filter((person) => person.recordId !== currentRecord?.recordId) || [],
    [allPersonsOnImage, currentRecord]
  )

  const allAvailablePersonsOptions = useMemo(
    () =>
      buildAllAvailablePersonsOptions({
        hasAdditionalPersonsOptions,
        currentRecordOrSharingFieldPersons,
        imagePersonsMinusCurrentRecord,
        allPersonsSharingField,
        allPersonIdsSharingFieldOnImages,
      }),
    [
      hasAdditionalPersonsOptions,
      currentRecordOrSharingFieldPersons,
      imagePersonsMinusCurrentRecord,
      allPersonsSharingField,
      allPersonIdsSharingFieldOnImages,
    ]
  )

  const allTheArks = useMemo(
    () =>
      allAvailablePersonsOptions?.reduce(
        (acc, person) => {
          if (!acc[person.partitionKey]) {
            acc[person.partitionKey] = [person]
          } else {
            acc[person.partitionKey].push(person)
          }
          return acc
        },
        {} /* e.g. { '3:1:etc': [...personsOnThisImage] }, '3:1:etc': [...personsOnOtherImage] } */
      ),
    [allAvailablePersonsOptions]
  )

  const allTheArksExceptCurrent = useMemo(() => {
    if (allTheArks) {
      const clone = cloneDeep(allTheArks)
      delete clone[state.currentImageArk]
      return clone
    }
    return {}
  }, [allTheArks, state.currentImageArk])

  const handleSubmit = (values) => {
    const { mainEditHighlights } = h.getHighlights()
    const fieldHiglight = mainEditHighlights.find((highlight) => highlight.fieldId === sharedElement.id)

    handleSubmitSharedFieldForm(
      values,
      selectedPerson,
      sharedElement,
      newValue,
      state,
      r2State,
      allAvailablePersonsOptions,
      handleSave,
      allPersonIdsSharingFieldOnImages,
      fieldHiglight
    )
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: getFormDefaultValues({
      personIds: allPersonIdsSharingFieldOnImages,
      persons: allPersonsSharingField,
      sharedElement,
      fieldDisplayValue,
    }),
  })

  useUnsavedChangesOverlay({ overlay: unsavedChangesOverlay, panelId: 'editSharedFieldPage2' })

  // 3) Display sections for all arks containing persons sharing field
  return (
    <>
      <ZionForm onSubmit={handleSubmit} methods={methods} id="edit-shared-field-page-2">
        <PanelHeader
          heading={title}
          caption={t('edit-shared-surname.instructions.heading', 'Fix any mistakes that you see.')}
          headingSize="H5"
        />
        <Separator />
        <H6>
          {lockSharedSuperEl
            ? t(
                'edit-shared-field.locked-others-on-image.heading',
                '3. These names are linked to this {fieldType}. They will be changed to "{newValue}".',
                {
                  newValue: newValueText,
                  fieldType: sharedElement?.typeData?.displayLabel,
                }
              )
            : t(
                'edit-shared-field.choose-others-on-image.heading',
                '3. These names are linked to this {fieldType}. Change them to "{newValue}"?',
                {
                  newValue: newValueText,
                  fieldType: sharedElement?.typeData?.displayLabel,
                }
              )}
        </H6>
        {allAvailablePersonsOptions?.map((person) => (
          <SharedFieldPersonCheckbox
            key={person.id}
            person={person}
            lockSharedSuperEl={lockSharedSuperEl}
            newValue={newValue}
            record={currentRecord}
            sharedElement={sharedElement}
          />
        ))}
        {allTheArksExceptCurrent &&
          renderOtherPagesSections({
            allTheArksExceptCurrent,
            thumbnails,
            currentArkIndex,
            t,
            r2Dispatch,
            lockSharedSuperEl,
            newValue,
            currentRecord,
            sharedElement,
          })}
      </ZionForm>
      <UnsavedChangesOverlay
        formId="edit-shared-field"
        onDiscard={onDiscard}
        onSave={() => saveButtonRef.current.click()}
        overlay={unsavedChangesOverlay}
      />
    </>
  )
}

export default SharedFieldPage2
